import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { Filter, X } from "lucide-react";

export const EMPTY_INVOICE_FILTERS = {
  customer_id: "",
  status: "",
  invoice_number: "",
  date_field: "due_date",
  start_date: "",
  end_date: "",
};

export const INVOICE_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "overdue", label: "Overdue" },
];

const todayIso = () => new Date().toISOString().split("T")[0];

export const isOverdue = (invoice) =>
  (invoice.status === "pending" || invoice.status === "sent" || invoice.status === "partial") && invoice.due_date && invoice.due_date < todayIso();

export const applyInvoiceFilters = (invoices, f) =>
  invoices.filter((inv) => {
    if (f.customer_id && inv.customer_id !== f.customer_id) return false;
    if (f.status === "overdue" ? !isOverdue(inv) : f.status && inv.status !== f.status) return false;
    if (f.invoice_number && !inv.invoice_number.toLowerCase().includes(f.invoice_number.toLowerCase())) return false;
    const dateValue = f.date_field === "created_at" ? String(inv.created_at || "").slice(0, 10) : inv.due_date || "";
    if (f.start_date && dateValue < f.start_date) return false;
    if (f.end_date && dateValue > f.end_date) return false;
    return true;
  });

export const InvoiceFilters = ({ filters, onChange, customers, resultCount, totalCount }) => {
  const set = (patch) => onChange({ ...filters, ...patch });
  const active = Object.entries(filters).some(([k, v]) => k !== "date_field" && v);

  return (
    <Card className="bg-white border-0 shadow-lg mb-6" data-testid="invoice-filters">
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <Label>Customer</Label>
            <CustomerCombobox customers={customers} value={filters.customer_id} onChange={(v) => set({ customer_id: v })} allowAll testId="invoice-filter-customer" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={filters.status || "all"} onValueChange={(v) => set({ status: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="invoice-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {INVOICE_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Invoice #</Label>
            <Input value={filters.invoice_number} onChange={(e) => set({ invoice_number: e.target.value })} placeholder="INV-00012" data-testid="invoice-filter-number" />
          </div>
          <div>
            <Label>Date Field</Label>
            <Select value={filters.date_field} onValueChange={(v) => set({ date_field: v })}>
              <SelectTrigger data-testid="invoice-filter-date-field"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="due_date">Due Date</SelectItem>
                <SelectItem value="created_at">Invoice Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="invoice-filter-start" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="invoice-filter-end" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500 inline-flex items-center gap-2" data-testid="invoice-result-count">
            <Filter className="w-4 h-4" /> Showing {resultCount} of {totalCount} invoices
          </p>
          {active && (
            <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_INVOICE_FILTERS)} data-testid="invoice-filters-clear">
              <X className="w-4 h-4 mr-1" /> Clear filters
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
